// 124 curated multi-stop gradients. Used by the Studio mode Color tab as
// instant starting points. Each gradient is a small array of { t, color }
// stops; Dotgrad samples them (in OKLab when enabled) to produce the rendered
// dot field.

export type Stop = { t: number; color: string };

export type Swatch = {
  id: string;
  name: string;
  stops: Stop[];
};

export const SWATCHES: readonly Swatch[] = [
  {
    id: 'spectrum',
    name: 'Spectrum',
    stops: [
      { t: 0,    color: '#ff0040' },
      { t: 0.17, color: '#ffaa00' },
      { t: 0.33, color: '#ffff00' },
      { t: 0.5,  color: '#00ff66' },
      { t: 0.67, color: '#00bbff' },
      { t: 0.83, color: '#7733ff' },
      { t: 1,    color: '#ff00aa' },
    ],
  },
  {
    id: 'sunset',
    name: 'Sunset',
    stops: [
      { t: 0,    color: '#0a0118' },
      { t: 0.4,  color: '#ff2e63' },
      { t: 0.7,  color: '#ff9a3c' },
      { t: 1,    color: '#fff4d2' },
    ],
  },
  {
    id: 'aurora',
    name: 'Aurora',
    stops: [
      { t: 0,    color: '#0a043c' },
      { t: 0.4,  color: '#03506f' },
      { t: 0.7,  color: '#23c7a9' },
      { t: 1,    color: '#bcffe5' },
    ],
  },
  {
    id: 'lava',
    name: 'Lava',
    stops: [
      { t: 0,    color: '#000000' },
      { t: 0.35, color: '#7a0000' },
      { t: 0.6,  color: '#ff3300' },
      { t: 0.85, color: '#ffcc00' },
      { t: 1,    color: '#ffffff' },
    ],
  },
  {
    id: 'ocean',
    name: 'Ocean',
    stops: [
      { t: 0,    color: '#000814' },
      { t: 0.4,  color: '#003566' },
      { t: 0.75, color: '#00b4d8' },
      { t: 1,    color: '#caf0f8' },
    ],
  },
  {
    id: 'forest',
    name: 'Forest',
    stops: [
      { t: 0,    color: '#1b1410' },
      { t: 0.4,  color: '#3a5a40' },
      { t: 0.75, color: '#a3b18a' },
      { t: 1,    color: '#fefae0' },
    ],
  },
  {
    id: 'cyber',
    name: 'Cyber',
    stops: [
      { t: 0,    color: '#0a0014' },
      { t: 0.5,  color: '#ff006e' },
      { t: 1,    color: '#00f0ff' },
    ],
  },
  {
    id: 'synthwave',
    name: 'Synthwave',
    stops: [
      { t: 0,    color: '#190a3c' },
      { t: 0.4,  color: '#a4138a' },
      { t: 0.75, color: '#ff5c6c' },
      { t: 1,    color: '#ffd166' },
    ],
  },
  {
    id: 'mono',
    name: 'Mono',
    stops: [
      { t: 0, color: '#000000' },
      { t: 1, color: '#ffffff' },
    ],
  },
  {
    id: 'inverted',
    name: 'Inverted',
    stops: [
      { t: 0, color: '#ffffff' },
      { t: 1, color: '#000000' },
    ],
  },
  {
    id: 'heat',
    name: 'Heat',
    stops: [
      { t: 0,    color: '#000000' },
      { t: 0.5,  color: '#cc0000' },
      { t: 0.85, color: '#ffd000' },
      { t: 1,    color: '#fffbf0' },
    ],
  },
  {
    id: 'cool',
    name: 'Cool',
    stops: [
      { t: 0,    color: '#000010' },
      { t: 0.5,  color: '#003a8c' },
      { t: 0.85, color: '#22a6ff' },
      { t: 1,    color: '#e5f9ff' },
    ],
  },
  {
    id: 'pastel',
    name: 'Pastel',
    stops: [
      { t: 0,    color: '#ffd6e0' },
      { t: 0.33, color: '#c1f0d4' },
      { t: 0.66, color: '#cbb7ff' },
      { t: 1,    color: '#fff6c2' },
    ],
  },
  {
    id: 'neon',
    name: 'Neon',
    stops: [
      { t: 0,    color: '#0a0a0a' },
      { t: 0.33, color: '#39ff14' },
      { t: 0.66, color: '#ff10f0' },
      { t: 1,    color: '#00ffff' },
    ],
  },
  {
    id: 'vintage',
    name: 'Vintage',
    stops: [
      { t: 0,    color: '#3a2718' },
      { t: 0.5,  color: '#c0a080' },
      { t: 1,    color: '#fff5e0' },
    ],
  },
  {
    id: 'wine',
    name: 'Wine',
    stops: [
      { t: 0,    color: '#0a0008' },
      { t: 0.5,  color: '#7a0033' },
      { t: 1,    color: '#ff6699' },
    ],
  },
  {
    id: 'electric',
    name: 'Electric',
    stops: [
      { t: 0,    color: '#000010' },
      { t: 0.5,  color: '#1a4cff' },
      { t: 1,    color: '#ffffff' },
    ],
  },
  {
    id: 'fire',
    name: 'Fire',
    stops: [
      { t: 0,    color: '#000000' },
      { t: 0.4,  color: '#ff4400' },
      { t: 0.75, color: '#ffaa00' },
      { t: 1,    color: '#ffffe0' },
    ],
  },
  {
    id: 'forest-depth',
    name: 'Forest Depth',
    stops: [
      { t: 0,    color: '#021a0a' },
      { t: 0.5,  color: '#108040' },
      { t: 1,    color: '#d4ff80' },
    ],
  },
  {
    id: 'berry',
    name: 'Berry',
    stops: [
      { t: 0,    color: '#1a0033' },
      { t: 0.5,  color: '#990066' },
      { t: 1,    color: '#ffaaee' },
    ],
  },
  {
    id: 'sky',
    name: 'Sky',
    stops: [
      { t: 0,    color: '#001a4d' },
      { t: 0.45, color: '#1a7fff' },
      { t: 0.8,  color: '#a5d8ff' },
      { t: 1,    color: '#ffffff' },
    ],
  },
  {
    id: 'coral',
    name: 'Coral',
    stops: [
      { t: 0,    color: '#ffd5b8' },
      { t: 0.5,  color: '#ff7a59' },
      { t: 1,    color: '#ff3d8b' },
    ],
  },
  {
    id: 'toxic',
    name: 'Toxic',
    stops: [
      { t: 0,    color: '#0a0a0a' },
      { t: 0.5,  color: '#33ff33' },
      { t: 1,    color: '#fff200' },
    ],
  },
  {
    id: 'candy',
    name: 'Candy',
    stops: [
      { t: 0,    color: '#ff80ab' },
      { t: 0.5,  color: '#ffb74d' },
      { t: 1,    color: '#fff59d' },
    ],
  },

  // ---- Greens ----
  { id: 'mint',          name: 'Mint',          stops: [{ t: 0, color: '#0a1f1a' }, { t: 0.5, color: '#3ec79a' }, { t: 1, color: '#d4ffe8' }] },
  { id: 'sage',          name: 'Sage',          stops: [{ t: 0, color: '#1f2820' }, { t: 0.5, color: '#7d9b76' }, { t: 1, color: '#e6efd9' }] },
  { id: 'moss',          name: 'Moss',          stops: [{ t: 0, color: '#0d1f0d' }, { t: 0.5, color: '#5a8a3a' }, { t: 1, color: '#dff5b8' }] },
  { id: 'fern',          name: 'Fern',          stops: [{ t: 0, color: '#0a1a08' }, { t: 0.5, color: '#4a7a30' }, { t: 1, color: '#dcf2a3' }] },
  { id: 'eucalyptus',    name: 'Eucalyptus',    stops: [{ t: 0, color: '#1a2b28' }, { t: 0.5, color: '#6a9a8a' }, { t: 1, color: '#dff0e8' }] },
  { id: 'olive',         name: 'Olive',         stops: [{ t: 0, color: '#1a1a0a' }, { t: 0.5, color: '#7a7a30' }, { t: 1, color: '#f0e8a0' }] },
  { id: 'matcha',        name: 'Matcha',        stops: [{ t: 0, color: '#1a2010' }, { t: 0.5, color: '#88a04a' }, { t: 1, color: '#f0f5d0' }] },
  { id: 'lime-cream',    name: 'Lime Cream',    stops: [{ t: 0, color: '#0a1a08' }, { t: 0.5, color: '#a0d040' }, { t: 1, color: '#fafff0' }] },
  { id: 'avocado',       name: 'Avocado',       stops: [{ t: 0, color: '#1f2010' }, { t: 0.5, color: '#7a8a3a' }, { t: 1, color: '#e8f0c0' }] },
  { id: 'shamrock',      name: 'Shamrock',      stops: [{ t: 0, color: '#001a0a' }, { t: 0.5, color: '#0a8a4a' }, { t: 1, color: '#c8f0d8' }] },

  // ---- Teals & blue-greens ----
  { id: 'peacock',       name: 'Peacock',       stops: [{ t: 0, color: '#001f2a' }, { t: 0.5, color: '#0a7a8a' }, { t: 1, color: '#a0e8e0' }] },
  { id: 'teal-deep',     name: 'Teal Deep',     stops: [{ t: 0, color: '#001a1f' }, { t: 0.5, color: '#0a8a8a' }, { t: 1, color: '#c0f0f0' }] },
  { id: 'lagoon',        name: 'Lagoon',        stops: [{ t: 0, color: '#001a2a' }, { t: 0.5, color: '#0a8aa0' }, { t: 1, color: '#c0f0e8' }] },
  { id: 'glacier',       name: 'Glacier',       stops: [{ t: 0, color: '#0a1a2a' }, { t: 0.5, color: '#5aa0c8' }, { t: 1, color: '#f0f8ff' }] },

  // ---- Cool blues ----
  { id: 'arctic',        name: 'Arctic',        stops: [{ t: 0, color: '#0a1a3a' }, { t: 0.5, color: '#80b8e0' }, { t: 1, color: '#ffffff' }] },
  { id: 'frost-blue',    name: 'Frost Blue',    stops: [{ t: 0, color: '#0a1a2f' }, { t: 0.5, color: '#9ac8e8' }, { t: 1, color: '#ffffff' }] },
  { id: 'blizzard',      name: 'Blizzard',      stops: [{ t: 0, color: '#1a2a3a' }, { t: 0.5, color: '#a0b8c8' }, { t: 1, color: '#ffffff' }] },
  { id: 'iceberg',       name: 'Iceberg',       stops: [{ t: 0, color: '#001a3a' }, { t: 0.5, color: '#80c0d8' }, { t: 1, color: '#e8faff' }] },
  { id: 'denim',         name: 'Denim',         stops: [{ t: 0, color: '#0a1a3a' }, { t: 0.5, color: '#3a5a8a' }, { t: 1, color: '#a0b8d0' }] },
  { id: 'cobalt',        name: 'Cobalt',        stops: [{ t: 0, color: '#000a3a' }, { t: 0.5, color: '#1a3acf' }, { t: 1, color: '#a0b8ff' }] },
  { id: 'royal',         name: 'Royal',         stops: [{ t: 0, color: '#1a0a3a' }, { t: 0.5, color: '#3a2acf' }, { t: 1, color: '#c0a0ff' }] },

  // ---- Atmospheric ----
  { id: 'midnight-blue', name: 'Midnight Blue', stops: [{ t: 0, color: '#000010' }, { t: 0.5, color: '#0a1a5a' }, { t: 1, color: '#5a7ac0' }] },
  { id: 'twilight',      name: 'Twilight',      stops: [{ t: 0, color: '#0f0a2f' }, { t: 0.5, color: '#5a3a8a' }, { t: 1, color: '#ffaad0' }] },
  { id: 'dusk',          name: 'Dusk',          stops: [{ t: 0, color: '#1a0a2a' }, { t: 0.4, color: '#6a3a7a' }, { t: 0.8, color: '#ff8a5a' }, { t: 1, color: '#ffd8a0' }] },
  { id: 'dawn',          name: 'Dawn',          stops: [{ t: 0, color: '#1a0a3a' }, { t: 0.4, color: '#a05a8a' }, { t: 0.8, color: '#ffaa6a' }, { t: 1, color: '#ffe8b0' }] },

  // ---- Warm orange/red ----
  { id: 'mango',         name: 'Mango',         stops: [{ t: 0, color: '#2a0a00' }, { t: 0.5, color: '#ff9a30' }, { t: 1, color: '#fff4d0' }] },
  { id: 'apricot',       name: 'Apricot',       stops: [{ t: 0, color: '#3a1a00' }, { t: 0.5, color: '#ffa860' }, { t: 1, color: '#fff0d8' }] },
  { id: 'peach',         name: 'Peach',         stops: [{ t: 0, color: '#3a1a10' }, { t: 0.5, color: '#ffb088' }, { t: 1, color: '#ffeada' }] },
  { id: 'tangerine',     name: 'Tangerine',     stops: [{ t: 0, color: '#2a0a00' }, { t: 0.5, color: '#ff7a1a' }, { t: 1, color: '#ffe89a' }] },
  { id: 'amber',         name: 'Amber',         stops: [{ t: 0, color: '#1a0a00' }, { t: 0.5, color: '#d8800a' }, { t: 1, color: '#fff0c0' }] },
  { id: 'honey',         name: 'Honey',         stops: [{ t: 0, color: '#2a1a00' }, { t: 0.5, color: '#e8a830' }, { t: 1, color: '#fff8c8' }] },
  { id: 'caramel',       name: 'Caramel',       stops: [{ t: 0, color: '#1a0a00' }, { t: 0.5, color: '#a86a30' }, { t: 1, color: '#f0d8b0' }] },
  { id: 'mocha',         name: 'Mocha',         stops: [{ t: 0, color: '#0a0500' }, { t: 0.5, color: '#6a3a20' }, { t: 1, color: '#d8b890' }] },
  { id: 'espresso',      name: 'Espresso',      stops: [{ t: 0, color: '#0a0500' }, { t: 0.5, color: '#3a1a08' }, { t: 1, color: '#a87858' }] },
  { id: 'cocoa',         name: 'Cocoa',         stops: [{ t: 0, color: '#1a0a00' }, { t: 0.5, color: '#5a2a18' }, { t: 1, color: '#c89878' }] },
  { id: 'cinnamon',      name: 'Cinnamon',      stops: [{ t: 0, color: '#1a0500' }, { t: 0.5, color: '#a83a18' }, { t: 1, color: '#f0c898' }] },

  // ---- Earth tones ----
  { id: 'taupe',         name: 'Taupe',         stops: [{ t: 0, color: '#1a1410' }, { t: 0.5, color: '#7a6850' }, { t: 1, color: '#e8d8c0' }] },
  { id: 'sand',          name: 'Sand',          stops: [{ t: 0, color: '#3a2a18' }, { t: 0.5, color: '#c0a880' }, { t: 1, color: '#fff0d0' }] },
  { id: 'dune',          name: 'Dune',          stops: [{ t: 0, color: '#2a1a08' }, { t: 0.5, color: '#a88858' }, { t: 1, color: '#f0e0b8' }] },
  { id: 'desert',        name: 'Desert',        stops: [{ t: 0, color: '#3a1a08' }, { t: 0.5, color: '#d09858' }, { t: 1, color: '#fff0c8' }] },
  { id: 'sahara',        name: 'Sahara',        stops: [{ t: 0, color: '#2a1000' }, { t: 0.5, color: '#e8a040' }, { t: 1, color: '#fff8d8' }] },
  { id: 'terracotta',    name: 'Terracotta',    stops: [{ t: 0, color: '#2a0a00' }, { t: 0.5, color: '#c0583a' }, { t: 1, color: '#f0c8a0' }] },
  { id: 'brick',         name: 'Brick',         stops: [{ t: 0, color: '#1a0500' }, { t: 0.5, color: '#a8482a' }, { t: 1, color: '#e8b898' }] },
  { id: 'rust',          name: 'Rust',          stops: [{ t: 0, color: '#1a0500' }, { t: 0.5, color: '#a8501a' }, { t: 1, color: '#e8b078' }] },

  // ---- Metallics ----
  { id: 'copper',        name: 'Copper',        stops: [{ t: 0, color: '#1a0a00' }, { t: 0.5, color: '#b86838' }, { t: 1, color: '#f8c8a0' }] },
  { id: 'bronze',        name: 'Bronze',        stops: [{ t: 0, color: '#1a1000' }, { t: 0.5, color: '#a87838' }, { t: 1, color: '#f0d098' }] },
  { id: 'gold-rich',     name: 'Gold Rich',     stops: [{ t: 0, color: '#1a1000' }, { t: 0.5, color: '#d0a020' }, { t: 1, color: '#fff0a8' }] },
  { id: 'brass',         name: 'Brass',         stops: [{ t: 0, color: '#1a1408' }, { t: 0.5, color: '#b89830' }, { t: 1, color: '#f0e0a0' }] },

  // ---- Yellow & cream ----
  { id: 'sunflower',     name: 'Sunflower',     stops: [{ t: 0, color: '#2a1800' }, { t: 0.5, color: '#f0c020' }, { t: 1, color: '#fff8c0' }] },
  { id: 'mustard',       name: 'Mustard',       stops: [{ t: 0, color: '#1a1000' }, { t: 0.5, color: '#c8a020' }, { t: 1, color: '#f8e8a0' }] },
  { id: 'lemon',         name: 'Lemon',         stops: [{ t: 0, color: '#1a1800' }, { t: 0.5, color: '#f0e030' }, { t: 1, color: '#ffffd8' }] },
  { id: 'butter',        name: 'Butter',        stops: [{ t: 0, color: '#2a2008' }, { t: 0.5, color: '#f0d878' }, { t: 1, color: '#fff8d8' }] },
  { id: 'ivory',         name: 'Ivory',         stops: [{ t: 0, color: '#3a3020' }, { t: 0.5, color: '#d8c8a8' }, { t: 1, color: '#fffaf0' }] },
  { id: 'champagne',     name: 'Champagne',     stops: [{ t: 0, color: '#2a2010' }, { t: 0.5, color: '#d8b878' }, { t: 1, color: '#fff0c8' }] },

  // ---- Pinks & corals ----
  { id: 'rose-gold',     name: 'Rose Gold',     stops: [{ t: 0, color: '#1a0a08' }, { t: 0.5, color: '#c8786a' }, { t: 1, color: '#f8d0c0' }] },
  { id: 'blush',         name: 'Blush',         stops: [{ t: 0, color: '#1a0a10' }, { t: 0.5, color: '#e89898' }, { t: 1, color: '#ffe0d8' }] },
  { id: 'dusty-rose',    name: 'Dusty Rose',    stops: [{ t: 0, color: '#1a0a18' }, { t: 0.5, color: '#a86878' }, { t: 1, color: '#e8c8c0' }] },
  { id: 'salmon',        name: 'Salmon',        stops: [{ t: 0, color: '#2a0a08' }, { t: 0.5, color: '#ff8868' }, { t: 1, color: '#ffd8c0' }] },
  { id: 'coral-pink',    name: 'Coral Pink',    stops: [{ t: 0, color: '#2a0a10' }, { t: 0.5, color: '#ff7a8a' }, { t: 1, color: '#ffd0d0' }] },
  { id: 'flamingo',      name: 'Flamingo',      stops: [{ t: 0, color: '#2a0a18' }, { t: 0.5, color: '#ff5a8a' }, { t: 1, color: '#ffc8d8' }] },
  { id: 'hibiscus',      name: 'Hibiscus',      stops: [{ t: 0, color: '#2a0008' }, { t: 0.5, color: '#e0285a' }, { t: 1, color: '#ffb8c8' }] },
  { id: 'peony',         name: 'Peony',         stops: [{ t: 0, color: '#2a0820' }, { t: 0.5, color: '#d83a8a' }, { t: 1, color: '#ffc8e0' }] },
  { id: 'magenta-glow',  name: 'Magenta Glow',  stops: [{ t: 0, color: '#1a0010' }, { t: 0.5, color: '#d80aa0' }, { t: 1, color: '#ffb0e0' }] },
  { id: 'fuchsia',       name: 'Fuchsia',       stops: [{ t: 0, color: '#1a0018' }, { t: 0.5, color: '#e80acf' }, { t: 1, color: '#ffb0e8' }] },
  { id: 'orchid',        name: 'Orchid',        stops: [{ t: 0, color: '#1a0a1a' }, { t: 0.5, color: '#a83ac8' }, { t: 1, color: '#e8b8f0' }] },
  { id: 'plum',          name: 'Plum',          stops: [{ t: 0, color: '#0a0010' }, { t: 0.5, color: '#5a1a6a' }, { t: 1, color: '#c898d8' }] },

  // ---- Purples ----
  { id: 'amethyst',      name: 'Amethyst',      stops: [{ t: 0, color: '#1a0a2a' }, { t: 0.5, color: '#7a3acf' }, { t: 1, color: '#d8b8f0' }] },
  { id: 'lavender',      name: 'Lavender',      stops: [{ t: 0, color: '#1a1030' }, { t: 0.5, color: '#9890d8' }, { t: 1, color: '#e8d8ff' }] },
  { id: 'lilac',         name: 'Lilac',         stops: [{ t: 0, color: '#1a0a2a' }, { t: 0.5, color: '#b098d8' }, { t: 1, color: '#f0e0ff' }] },
  { id: 'mauve',         name: 'Mauve',         stops: [{ t: 0, color: '#1a0a18' }, { t: 0.5, color: '#9878a0' }, { t: 1, color: '#e0c8d8' }] },
  { id: 'periwinkle',    name: 'Periwinkle',    stops: [{ t: 0, color: '#0a0a3a' }, { t: 0.5, color: '#8090e0' }, { t: 1, color: '#d8e0ff' }] },
  { id: 'iris',          name: 'Iris',          stops: [{ t: 0, color: '#0a0830' }, { t: 0.5, color: '#5a48b8' }, { t: 1, color: '#c8c0f0' }] },
  { id: 'violet-deep',   name: 'Violet Deep',   stops: [{ t: 0, color: '#0a0028' }, { t: 0.5, color: '#3a18a8' }, { t: 1, color: '#a890f0' }] },
  { id: 'indigo-deep',   name: 'Indigo Deep',   stops: [{ t: 0, color: '#000018' }, { t: 0.5, color: '#1a18a0' }, { t: 1, color: '#9088e0' }] },

  // ---- Cosmic ----
  { id: 'galaxy',        name: 'Galaxy',        stops: [{ t: 0, color: '#000010' }, { t: 0.5, color: '#5a18a8' }, { t: 1, color: '#ff8ad0' }] },
  { id: 'nebula',        name: 'Nebula',        stops: [{ t: 0, color: '#000010' }, { t: 0.4, color: '#a818a8' }, { t: 0.75, color: '#1a78ff' }, { t: 1, color: '#a8e0ff' }] },
  { id: 'supernova',     name: 'Supernova',     stops: [{ t: 0, color: '#000000' }, { t: 0.35, color: '#a80018' }, { t: 0.7, color: '#ffc818' }, { t: 1, color: '#ffffff' }] },
  { id: 'blackhole',     name: 'Black Hole',    stops: [{ t: 0, color: '#000000' }, { t: 0.5, color: '#3a0a4a' }, { t: 1, color: '#000000' }] },
  { id: 'void',          name: 'Void',          stops: [{ t: 0, color: '#000000' }, { t: 0.5, color: '#1a1a1a' }, { t: 1, color: '#000000' }] },
  { id: 'plasma',        name: 'Plasma',        stops: [{ t: 0, color: '#000010' }, { t: 0.35, color: '#c818a8' }, { t: 0.7, color: '#ff8830' }, { t: 1, color: '#fff8a0' }] },
  { id: 'vapor',         name: 'Vapor',         stops: [{ t: 0, color: '#000010' }, { t: 0.5, color: '#ff48d8' }, { t: 1, color: '#48f0ff' }] },
  { id: 'holo',          name: 'Holo',          stops: [{ t: 0, color: '#000018' }, { t: 0.33, color: '#48ffff' }, { t: 0.66, color: '#ff48ff' }, { t: 1, color: '#ffff48' }] },
  { id: 'retrowave',     name: 'Retrowave',     stops: [{ t: 0, color: '#1a0030' }, { t: 0.4, color: '#c818c8' }, { t: 0.75, color: '#ff5a3a' }, { t: 1, color: '#ffe040' }] },
  { id: 'cyberpunk',     name: 'Cyberpunk',     stops: [{ t: 0, color: '#000010' }, { t: 0.5, color: '#18e8ff' }, { t: 1, color: '#ff18a8' }] },
  { id: 'matrix',        name: 'Matrix',        stops: [{ t: 0, color: '#000000' }, { t: 0.5, color: '#10b830' }, { t: 1, color: '#a8ffc0' }] },
  { id: 'glitch',        name: 'Glitch',        stops: [{ t: 0, color: '#000000' }, { t: 0.33, color: '#ff1830' }, { t: 0.66, color: '#18ffe8' }, { t: 1, color: '#48ff18' }] },
  { id: 'acid',          name: 'Acid',          stops: [{ t: 0, color: '#000800' }, { t: 0.5, color: '#48ff10' }, { t: 1, color: '#fffa10' }] },
  { id: 'radioactive',   name: 'Radioactive',   stops: [{ t: 0, color: '#000800' }, { t: 0.5, color: '#a8ff10' }, { t: 1, color: '#fffa48' }] },
  { id: 'blueprint',     name: 'Blueprint',     stops: [{ t: 0, color: '#0a1a48' }, { t: 0.5, color: '#3a78c8' }, { t: 1, color: '#f0f8ff' }] },

  // ---- Greys ----
  { id: 'graphite',      name: 'Graphite',      stops: [{ t: 0, color: '#000000' }, { t: 0.5, color: '#3a3a40' }, { t: 1, color: '#b0b0b8' }] },
  { id: 'slate',         name: 'Slate',         stops: [{ t: 0, color: '#000000' }, { t: 0.5, color: '#48586a' }, { t: 1, color: '#b8c0c8' }] },
  { id: 'charcoal',      name: 'Charcoal',      stops: [{ t: 0, color: '#000000' }, { t: 0.5, color: '#2a2a2a' }, { t: 1, color: '#a0a0a0' }] },
  { id: 'ash',           name: 'Ash',           stops: [{ t: 0, color: '#0a0a0a' }, { t: 0.5, color: '#5a5a5a' }, { t: 1, color: '#d8d8d8' }] },
  { id: 'fog',           name: 'Fog',           stops: [{ t: 0, color: '#1a1a1a' }, { t: 0.5, color: '#7a8088' }, { t: 1, color: '#e0e8f0' }] },
  { id: 'smoke',         name: 'Smoke',         stops: [{ t: 0, color: '#1a1a18' }, { t: 0.5, color: '#787878' }, { t: 1, color: '#e0d8d0' }] },
  { id: 'mist',          name: 'Mist',          stops: [{ t: 0, color: '#1a1a20' }, { t: 0.5, color: '#9098a8' }, { t: 1, color: '#f0f4fa' }] },

  // ---- Neutrals ----
  { id: 'cream',         name: 'Cream',         stops: [{ t: 0, color: '#3a3020' }, { t: 0.5, color: '#e0d0a8' }, { t: 1, color: '#fffaf0' }] },
  { id: 'pearl',         name: 'Pearl',         stops: [{ t: 0, color: '#2a2a3a' }, { t: 0.5, color: '#c8d0e0' }, { t: 1, color: '#fffaff' }] },
  { id: 'opal',          name: 'Opal',          stops: [{ t: 0, color: '#000010' }, { t: 0.33, color: '#48b8d8' }, { t: 0.66, color: '#e8a0d8' }, { t: 1, color: '#fff8e8' }] },
  { id: 'obsidian',      name: 'Obsidian',      stops: [{ t: 0, color: '#000000' }, { t: 0.5, color: '#1a1828' }, { t: 1, color: '#48485a' }] },
];

export function getSwatch(id: string): Swatch | undefined {
  return SWATCHES.find((s) => s.id === id);
}
